import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  IFilters,
  IPagination2,
  ISorting,
} from "../../../interfaces/Components/ITable";
import { useTablePreferences } from "../../../hooks/useTablePreferences";
import "../../../assets/css/table/Table.css";
import "../../../assets/css/table/Pagination.css";
import ExcelModal from "../../Atomos/Modals/ExcelModal";
import { axios } from "../../..";
import useDataLoader from "../../../hooks/useDataLoader";
import { convertDateRangeToEpoch } from "../../../utils/functions";
import { DropDownOptionBase } from "../../staff/DropDownMultiple";
import QuickFiltersBar from "./QuickFiltersBar";
import FiltersPanel from "./FiltersPanel";
import TableSection from "./TableSection";
import {
  applyCustomFilterClausesToQuery,
  buildEmptyFilterValues,
  buildExcelFilename,
  buildQueryParamsPersistentFilterOnly,
  collectCustomFilterDefaults,
  COLUMN_VISIBILITY_PANEL_IDS,
  getFixedColumnPrefixIds,
  getExcelColumnTitle,
  getVisibleExcelColumns,
  coalesceGroupedColumnOrder,
  isSingleCustomFilterClause,
  moveGroupedColumnOrder,
  normalizeColumnOrder,
  nextSort,
  safeExcelValue,
  isFilterHiddenFromUi,
} from "./FilteredTable.utils";
import {
  applyChipToFilterValues,
  applyChipToQuery,
  buildQuickFilterDefs,
  ensureChipHiddenQuery,
  findQuickFilter,
  isQuickFilterSelectionResolved,
  stripChipFromFilterValues,
  stripChipFromQuery,
} from "./FilteredTable.quickFilter";
import {
  exportStyledExcel,
  getExcelTitleFromFilename,
} from "../../../utils/excel/exportStyledExcel";
import {
  IFilterData,
  IGroupByConfig,
  IHeader,
  IQuickFilter,
  IRowSelectionConfig,
  ISelectedFilterGroup,
  CUSTOM_FILTER_CHIP_ID,
  type CustomFilterApplyPayload,
  type CustomFilterQueryClause,
  type FiltersPanelLayout,
  PanelFilterResetInfo,
  type TableBannerContent,
} from "./FilteredTable.types";
import {
  buildSyntheticColumnHeaders,
  getIndexColumnDefinition,
  getSelectionColumnDefinition,
} from "./FilteredTableColumnPresets";
import {
  FilteredTableSelectionProvider,
  type FilteredTableSelectionContextValue,
} from "./FilteredTableSelectionContext";
import {
  DEFAULT_TABLE_PAGE_SIZE,
  normalizeTablePageSize,
} from "../../../constants/tablePagination";

export interface QueryValue {
  value: string | string[];
  comparisonType?: string;
  arraySearchField?: string;
}

/**
 * Props for {@link FilteredTableCore}. Column and filter types live in `./FilteredTable.types`.
 *
 * @remarks
 * **Using this on a new screen**
 *
 * 1. Define `columnsDef` (`IHeader[]`): `id` should match API JSON fields (or fields produced by `formatData`). Use `cell` for custom rendering; set `enableSorting: false` where needed. Optional `columnOrderFixed` on a contiguous prefix pins those columns to the left (no drag, nothing before them). Optional `headerGroup` on contiguous columns renders a two-row grouped header (parent label + sub-column labels); sorting applies to leaf columns and drag-reorder moves the whole group together. Optional `headerTooltip` / `headerGroupTooltip` show the full label on hover when the visible header is abbreviated.
 * 2. Pass `endpoint` as the relative path used by `axios.Get` (e.g. `"/registration"`). `useDataLoader` builds the request.
 * 3. Optional `persistentFilter`: fixed conditions (e.g. firm on Professionals) merged into the API filter with panel state. Optional `mergePanelFiltersIntoPersistentFilter` (default false) also folds panel filters into `persistentFilter` for the list request; chip totals always ignore panel filters. Enable only where the list must follow panel + search (e.g. AOP Mapping).
 * 4. Define `filtersData` (`IFilterData[]`) for the side panel: `name` is the UI state key; `fieldName` is the key sent in the API filter object (if omitted, `name` lowercased is used). `comparison` and `type` control how values are serialized (see `buildQueryValue` in this file).
 * 5. Optional `quickFilters`: chips that apply `panelValues` (shown in Selected Filters) and `hiddenQuery` (API-only). Totals are fetched once from the original chip definitions and do not change when panel filters change. Optional `quickFilterRequireSelection` (default false) keeps at least one chip selected and prevents turning the active chip off. Optional `stickyQuickFilterBar` (default false) pins the chip row through filters and the table with column headers stacking below it (only when the top bar is shown). Optional `quickFilterBarPosition`: `top` (default), `belowSearch`, or `both` to also render chips below the search row. The bar also renders when only `quickFilterBarAccessory` is set (no chips).
 * 6. Optional `formatData` maps each row before render; for Excel “current page” export it is used when `formatExcelData` is not provided. Optional `formatExcelData` is dedicated sync/async shaping for Excel; otherwise raw rows or `formatData` apply.
 * 7. Layout slots: `quickFilterBarAccessory` (right of the quick-filter bar), `searchToolbarAccessory` (next to the search field), `belowSearchContent` (extra content below search when chips are not moved there), `topContent` (above the table inside `TableSection`), `bannerContent` (highlighted block above the filters panel, e.g. a dashboard).
 * 8. Optional `showRowNumber` and `rowSelection`: prefijan columnas fijas de índice (`#`) y/o selección por fila (página actual).
 * 9. Optional `groupBy` (`IGroupByConfig`): renders a subtitle row per distinct value of `groupBy.field`, with all matching rows underneath. At most one grouping per table. `additionalFields` (optional) adds an OR condition — a row is also included in a group when one of those fields matches the group's key, so the same row can appear under more than one subtitle. This is a display-only transform; it does not affect `formatData`, row selection, sorting, pagination, or Excel export.
 *
 */
interface ITableProps {
  containerclassName?: string;
  /**
   * Table rendered inside a nested scroll container (e.g. Modal). Removes top/side
   * panel padding and passes sticky context to `TableSection`.
   */
  embeddedInScrollContainer?: boolean;
  /**
   * Key for persisted table preferences (sorting, page size, filters). Defaults to the
   * current route pathname. Pass `null` to skip persistence (e.g. tables inside modals).
   */
  tableKey?: string | null;
  /** Specialized filters toolbar layout (e.g. firm logs modal). */
  filtersPanelLayout?: FiltersPanelLayout;
  columnsDef: IHeader[];
  showRowNumber?: boolean;
  rowSelection?: IRowSelectionConfig;
  /** At most one grouping per table: renders a subtitle row per distinct `groupBy.field` value. */
  groupBy?: IGroupByConfig;
  /**
   * When set, skips the API and filters, sorts, and paginates this array in the browser.
   * `formatData` runs on the full array before search/sort/pagination.
   */
  clientData?: any[];
  endpoint: string;
  showColumnVisibility?: boolean;
  showExcelExport?: boolean;
  /** Hides the selected-filter summary without disabling filtering. */
  showSelectedFilters?: boolean;
  /** Places visible filters, search, export, and column controls in one responsive row. */
  inlineFilterToolbar?: boolean;
  quickFilterBarAccessory?: React.ReactNode;
  /** Rendered on the left of the search field, in the same row. */
  searchLeading?: React.ReactNode;
  searchToolbarAccessory?: React.ReactNode;
  /** Optional content below the search row in the filters panel (merged with quick filters when `quickFilterBarPosition` is `belowSearch`). */
  belowSearchContent?: React.ReactNode;
  bottomPagination?: boolean;
  topPagination?: boolean;
  excelHeaders?: string[];
  formatExcelData?: (data: any[], isCurrentData?: boolean) => any[] | Promise<any[]>;
  formatData?: (data: any[]) => any[];
  emptyStateMessage?: string | React.ReactNode;
  excelFilename?: string;
  defaultColumnVisibility?: Record<string, boolean>;
  topContent?: React.ReactNode;
  /**
   * Highlighted block above the filters panel. May be a render function receiving
   * `quickFilterTotals` (original chip counts from page load; not affected by panel filters).
   */
  bannerContent?: TableBannerContent;
  filtersData: IFilterData[];
  defaultPageSize?: number;
  persistPageSize?: boolean;
  /**
   * When false, sorting is not read from or written to table preferences.
   * Use for embedded modal tables that should not share sort state with the parent page.
   * @default true
   */
  persistSorting?: boolean;
  defaultSorting?: ISorting;
  /**
   * When true and the user has no saved sort for this `tableKey`, persist `defaultSorting`
   * to table preferences so the default order is restored on later visits.
   */
  seedDefaultSortingPreference?: boolean;
  persistentFilter?: any;
  /**
   * When true, merges `valueForQuery` (without the chip key) into `persistentFilter` for the list request.
   * Chip totals always use the original chip definitions and ignore panel filters.
   */
  mergePanelFiltersIntoPersistentFilter?: boolean;
  /**
   * When false, panel `inputFilters` and API `filters` are not read from or written to table preferences.
   * Each visit starts from empty in-memory state so `defaultQuickFilterSeed` / quick-filter initialization apply.
   * Sorting, page size, and column preferences still persist.
   * @default true
   */
  persistFilters?: boolean;
  /**
   * When set (e.g. `1`), layout is `[#]` + first N data columns + `[checkbox]` + remaining data columns.
   * Default: all synthetic columns (`#` / checkbox) before `columnsDef` (legacy).
   */
  selectionInsertAfterDataColumns?: number;
  quickFilters?: IQuickFilter[];
  /**
   * Where to render the quick-filter chip row. `top` (default) above the filters panel;
   * `belowSearch` inside the panel under the search/export row; `both` renders in both places.
   * @default "top"
   */
  quickFilterBarPosition?: "top" | "belowSearch" | "both";
  /**
   * When true, the quick-filter chip row stays sticky while scrolling filters and the grid; column headers stick directly beneath it.
   * Only applies to the top quick-filter bar.
   * @default false
   */
  stickyQuickFilterBar?: boolean;
  quickFilterRequireSelection?: boolean;
  showSearchInput?: boolean;
  searchPlaceholder?: string;
  /** Fired when the active quick-filter chip changes (immediate UI state — not debounced like saved table prefs). */
  onQuickFilterChange?: (quickFilterKey: string) => void;
  /**
   * When the Year panel filter changes, optionally switch the active quick-filter chip
   * (e.g. past year id → `pastLive` while on `upcomingLive`). Return null to leave unchanged.
   */
  coerceQuickFilterOnYearChange?: (
    yearId: string | null | undefined,
    currentQuickFilter: string | null,
  ) => string | null;
  /**
   * Fired whenever the assembled server-side filter (`valueForQuery`) changes.
   * Lets a parent run its own query (e.g. aggregate stats) with the same panel filters.
   * Custom filters that call `apply(value, null)` do not appear here.
   */
  onQueryFilterChange?: (filter: Record<string, unknown>) => void;
  /**
   * Fired when panel filters are cleared (all chips) or a custom filter chip is removed.
   * Lets parents reset local state kept outside `filterValues` (e.g. Accounting fiscal year).
   */
  onPanelFilterReset?: (resets: PanelFilterResetInfo[]) => void;
  /**
   * Fired whenever the active sort changes (and on mount / after prefs hydration).
   * Lets a parent apply client-side sorting for columns whose values are computed
   * client-side (e.g. billing aggregates merged in `formatData`) and therefore
   * cannot be sorted by the server.
   */
  onSortingChange?: (sorting: ISorting) => void;
  /** Fired when the server-reported total row count changes after a fetch. */
  onTotalCountChange?: (totalCount: number) => void;
  /**
   * Per quick-filter default sort when the active chip changes.
   * By default only applies when the user has no saved `tablePreferences.sorting`.
   */
  quickFilterDefaultSorting?: Record<string, ISorting>;
  /**
   * When true, always apply `quickFilterDefaultSorting` on chip change (and persist it),
   * even if the user has a saved sort — use when each chip needs a different Starting Date order.
   */
  applyQuickFilterSortOnChipChange?: boolean;
  /**
   * Merged into panel/query state when `quickFilterRequireSelection` applies its initial chip fallback.
   * Use to pre-select panel filters (e.g. default multiselect values) together with the first quick filter.
   * Parent should stabilize the object (e.g. `useMemo`) to avoid unnecessary effect runs.
   */
  defaultQuickFilterSeed?: {
    inputFilters?: Record<string, unknown>;
    query?: Record<string, QueryValue>;
  };
  /**
   * Panel filter values always merged into `filterValues` (e.g. route-scoped Event on unpaids).
   * Applied after prefs hydration so the UI shows filters that `persistentFilter` already enforces.
   */
  pinnedInputFilters?: Record<string, unknown>;
  /**
   * When true, shows a draggable horizontal scroll bar above the table header when content overflows.
   * @default false
   */
  showTopHorizontalScrollBar?: boolean;
}

type ComparisonType =
  | "CONTAINS"
  | "BY_NAME"
  | "BY_NAME_CONTAINS"
  | "BY_ID"
  | "EQUAL"
  | "ARRAY_SEARCH_ID"
  | "ARRAY_CONTAINS"
  | "BOOLEAN"
  | "BETWEEN"
  | "BETWEEN_TS"
  | "BETWEEN_TS_PRESET"
  | "IN"
  | "NOT_EQUAL_OR_NULL"
  | "NOT_IN"
  | "SPONSOR_NAME_TOKENS"
  | "IS_DEFINED";

/** Multiselect-driven modes: empty `[]` must remove the filter key (do not send `value: []` to the API). */
const COMPARISON_CLEAR_WHEN_SELECTION_EMPTY = new Set<ComparisonType>([
  "BY_ID",
  "EQUAL",
  "IN",
  "BY_NAME",
  "BY_NAME_CONTAINS",
  "ARRAY_SEARCH_ID",
  "ARRAY_CONTAINS",
  "NOT_EQUAL_OR_NULL",
  "NOT_IN",
  "BOOLEAN",
  "SPONSOR_NAME_TOKENS",
  "IS_DEFINED",
]);

function asFilterRecordForPersistentMerge (value: unknown): Record<string, unknown> {
  if (value == null || Array.isArray(value)) {
    return {};
  }
  if (typeof value === "object") {
    return value as Record<string, unknown>;
  }
  return {};
}

function compareClientRowValues (a: unknown, b: unknown): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return -1;
  }
  if (b == null) {
    return 1;
  }
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  const aStr = String(a).toLowerCase();
  const bStr = String(b).toLowerCase();
  return aStr.localeCompare(bStr, undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/**
 * Remote-data filtered table: filter panel, optional search, quick filters, pagination, sorting,
 * draggable columns, and Excel export. Filter state is not controlled from the parent; query state lives
 * here and syncs with per-route table preferences.
 */
export default function FilteredTableCore ({
  containerclassName,
  embeddedInScrollContainer = false,
  tableKey: tableKeyProp,
  filtersPanelLayout = "default",
  columnsDef,
  showRowNumber = false,
  rowSelection,
  groupBy,
  endpoint,
  showColumnVisibility = true,
  showExcelExport = true,
  showSelectedFilters = true,
  inlineFilterToolbar = false,
  quickFilterBarAccessory,
  searchLeading,
  searchToolbarAccessory,
  belowSearchContent,
  bottomPagination = true,
  topPagination = false,
  excelHeaders,
  formatExcelData,
  formatData,
  emptyStateMessage,
  excelFilename: excelFilenameProp,
  defaultColumnVisibility,
  topContent,
  filtersData,
  defaultPageSize,
  persistPageSize = true,
  persistSorting = true,
  defaultSorting,
  seedDefaultSortingPreference = false,
  quickFilters = [],
  quickFilterBarPosition = "top",
  stickyQuickFilterBar = false,
  quickFilterRequireSelection = false,
  showSearchInput = false,
  searchPlaceholder,
  bannerContent,
  persistentFilter,
  mergePanelFiltersIntoPersistentFilter = false,
  persistFilters = true,
  selectionInsertAfterDataColumns,
  onQuickFilterChange,
  coerceQuickFilterOnYearChange,
  onQueryFilterChange,
  onPanelFilterReset,
  onSortingChange,
  onTotalCountChange,
  defaultQuickFilterSeed,
  pinnedInputFilters,
  quickFilterDefaultSorting,
  applyQuickFilterSortOnChipChange = false,
  showTopHorizontalScrollBar = false,
  clientData,
}: ITableProps): React.ReactElement {
  const isClientDataMode = clientData != null;
  const { pathname } = useLocation();
  const effectiveTableKey =
    tableKeyProp !== undefined ? tableKeyProp : pathname;
  const { tablePrefs, updateTablePrefs, loading: tablePrefsLoading } =
    useTablePreferences(effectiveTableKey);
  const tablePrefsReady = !effectiveTableKey || !tablePrefsLoading;
  const [openExcelModal, setOpenExcelModal] = useState(false);

  const hasRowSelection = rowSelection != null;
  const effectiveColumnsDef = useMemo(() => {
    const insertAfter =
      selectionInsertAfterDataColumns != null &&
        selectionInsertAfterDataColumns >= 0
        ? selectionInsertAfterDataColumns
        : null;

    if (insertAfter != null) {
      const indexPart = showRowNumber ? [getIndexColumnDefinition()] : [];
      const first = columnsDef.slice(0, insertAfter);
      const rest = columnsDef.slice(insertAfter);
      const selectionPart = hasRowSelection
        ? [getSelectionColumnDefinition(rowSelection?.headerLabel)]
        : [];
      return [...indexPart, ...first, ...selectionPart, ...rest];
    }

    const prefix = buildSyntheticColumnHeaders({
      showRowNumber,
      showSelection: hasRowSelection,
      selectionHeaderLabel: rowSelection?.headerLabel,
    });
    return [...prefix, ...columnsDef];
  }, [
    columnsDef,
    showRowNumber,
    hasRowSelection,
    rowSelection?.headerLabel,
    selectionInsertAfterDataColumns,
  ]);

  const defaultOrder = useMemo(
    () => effectiveColumnsDef.map((h) => h.id),
    [effectiveColumnsDef],
  );
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultOrder);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [measuredWidths, setMeasuredWidths] = useState<Record<string, number>>({});
  const [resizedWidths, setResizedWidths] = useState<Record<string, number>>({});
  const hydratedColumnPrefs = useRef(false);
  const hydratedColumnWidthsKeyRef = useRef<string | null>(null);
  const hydratedFromTablePrefs = useRef(false);
  const userHasChangedSortingRef = useRef(false);
  const appliedPrefsSortingKeyRef = useRef<string | null>(null);
  const prevEffectiveTableKeyRef = useRef<string | null>(null);
  const tablePrefsRef = useRef(tablePrefs);
  tablePrefsRef.current = tablePrefs;

  useEffect(() => {
    const prev = prevEffectiveTableKeyRef.current;
    if (prev !== null && prev !== effectiveTableKey) {
      hydratedColumnPrefs.current = false;
      hydratedColumnWidthsKeyRef.current = null;
      hydratedFromTablePrefs.current = false;
      userHasChangedSortingRef.current = false;
      appliedPrefsSortingKeyRef.current = null;
      setResizedWidths({});
    }
    prevEffectiveTableKeyRef.current = effectiveTableKey;
  }, [effectiveTableKey]);

  const emptyFilterValues = useMemo(
    () => buildEmptyFilterValues(filtersData, showSearchInput),
    [filtersData, showSearchInput],
  );

  const [filterImmediateFlushKey, setFilterImmediateFlushKey] = useState(0);
  /**
   * When a quick filter is required, seed the default chip into the very first state so the
   * initial data request is already filtered. Otherwise `useDataLoader` would fire a redundant
   * unfiltered request (all rows, full page size) before the quick-filter effect applies the chip.
   */
  const [valueForQuery, setValueForQueryRaw] = useState<any>(() => {
    const customQuery = collectCustomFilterDefaults(filtersData).query;
    const merged: Record<string, unknown> = {
      ...(defaultQuickFilterSeed?.query ?? {}),
      ...customQuery,
    };
    if (quickFilterRequireSelection && quickFilters.length > 0) {
      Object.assign(
        merged,
        applyChipToQuery({}, quickFilters[0], filtersData),
      );
    }
    return Object.keys(merged).length > 0 ? merged : null;
  });
  const [filterValues, setFilterValuesRaw] = useState<any>(() => {
    const base = buildEmptyFilterValues(filtersData, showSearchInput);
    const customInput = collectCustomFilterDefaults(filtersData).inputFilters;
    const mergedInput = {
      ...(defaultQuickFilterSeed?.inputFilters ?? {}),
      ...customInput,
    };
    if (quickFilterRequireSelection && quickFilters.length > 0) {
      return applyChipToFilterValues(
        { ...base, ...mergedInput },
        quickFilters[0],
      );
    }
    if (Object.keys(mergedInput).length === 0) {
      return {};
    }
    return { ...base, ...mergedInput };
  });
  const [pagination, setPaginationRaw] = useState<IPagination2>({
    pageIndex: 0,
    pageSize: normalizeTablePageSize(defaultPageSize ?? DEFAULT_TABLE_PAGE_SIZE),
  });
  const [sorting, setSortingRaw] = useState<ISorting>(
    defaultSorting ?? { field: "id", order: "asc" },
  );

  useEffect(() => {
    // Wait for API prefs (sorting/pageSize) the same way column prefs do.
    // Otherwise first paint locks onto `defaultSorting` and never applies saved sort.
    if (!tablePrefsReady) {
      return;
    }
    if (hydratedFromTablePrefs.current) {
      return;
    }
    const hasSortingPref = persistSorting && tablePrefs?.sorting;
    const hasPageSizePref =
      persistPageSize && tablePrefs?.pageSize !== undefined;
    const hasFiltersPref =
      persistFilters &&
      tablePrefs?.filters != null &&
      Object.keys(tablePrefs.filters).length > 0;
    const hasInputFiltersPref =
      persistFilters &&
      tablePrefs?.inputFilters != null &&
      Object.keys(tablePrefs.inputFilters).length > 0;
    const seedDefaultSortIfNeeded = () => {
      if (
        hasSortingPref ||
        !seedDefaultSortingPreference ||
        !persistSorting ||
        !effectiveTableKey ||
        !defaultSorting?.field
      ) {
        return;
      }
      appliedPrefsSortingKeyRef.current = `${defaultSorting.field}:${defaultSorting.order}`;
      updateTablePrefs({ sorting: defaultSorting }).catch(() => { });
    };

    if (
      !hasSortingPref &&
      !hasPageSizePref &&
      !hasFiltersPref &&
      !hasInputFiltersPref
    ) {
      hydratedFromTablePrefs.current = true;
      seedDefaultSortIfNeeded();
      return;
    }
    hydratedFromTablePrefs.current = true;
    if (tablePrefs.sorting) {
      const pref = tablePrefs.sorting;
      appliedPrefsSortingKeyRef.current = `${pref.field}:${pref.order}`;
      setSortingRaw(pref);
    } else {
      seedDefaultSortIfNeeded();
    }
    if (hasPageSizePref && tablePrefs.pageSize !== undefined) {
      setPaginationRaw({
        pageIndex: 0,
        pageSize: normalizeTablePageSize(
          tablePrefs.pageSize ?? defaultPageSize ?? DEFAULT_TABLE_PAGE_SIZE,
        ),
      });
    }
    if (hasFiltersPref && tablePrefs.filters) {
      const saved = {
        ...(tablePrefs.filters as Record<string, unknown>),
      };
      const savedChipKey =
        saved.quickFilter != null && String(saved.quickFilter) !== ""
          ? String(saved.quickFilter)
          : null;
      delete saved.quickFilter;
      const chip =
        findQuickFilter(quickFilters, savedChipKey) ??
        (quickFilterRequireSelection ? quickFilters[0] : undefined);
      setValueForQueryRaw(
        chip ? applyChipToQuery(saved, chip, filtersData) : saved,
      );
    }
    if (hasInputFiltersPref && tablePrefs.inputFilters) {
      const saved = {
        ...emptyFilterValues,
        ...(tablePrefs.inputFilters as Record<string, unknown>),
      };
      const savedChipKey =
        saved.quickFilter != null && String(saved.quickFilter) !== ""
          ? String(saved.quickFilter)
          : null;
      const chip =
        findQuickFilter(quickFilters, savedChipKey) ??
        (quickFilterRequireSelection ? quickFilters[0] : undefined);
      setFilterValuesRaw(
        chip ? applyChipToFilterValues(saved, chip) : saved,
      );
    }
  }, [
    effectiveTableKey,
    tablePrefs,
    tablePrefsReady,
    defaultPageSize,
    defaultSorting,
    persistPageSize,
    persistSorting,
    persistFilters,
    seedDefaultSortingPreference,
    updateTablePrefs,
    emptyFilterValues,
    quickFilters,
    quickFilterRequireSelection,
    filtersData,
  ]);

  // Prefs can arrive after an empty hydration (e.g. auth cleared prefs then fetch
  // completed). Apply saved sorting whenever it shows up, unless the user already sorted.
  useEffect(() => {
    if (!persistSorting || !effectiveTableKey || !tablePrefsReady) {
      return;
    }
    if (userHasChangedSortingRef.current) {
      return;
    }
    const pref = tablePrefs?.sorting;
    if (!pref?.field) {
      return;
    }

    const key = `${pref.field}:${pref.order}`;
    if (appliedPrefsSortingKeyRef.current === key) {
      return;
    }
    appliedPrefsSortingKeyRef.current = key;

    setSortingRaw((current) =>
      current.field === pref.field && current.order === pref.order ? current : pref,
    );
  }, [persistSorting, effectiveTableKey, tablePrefsReady, tablePrefs?.sorting]);

  const setValueForQuery = useCallback(
    (action: React.SetStateAction<any>) => {
      setValueForQueryRaw((prev: any) => {
        const next = typeof action === "function" ? action(prev) : action;
        if (effectiveTableKey && hydratedFromTablePrefs.current && persistFilters) {
          updateTablePrefs({
            filters: (next ?? {}) as Record<string, IFilters>,
          }).catch(() => { });
        }
        return next;
      });
    },
    [effectiveTableKey, updateTablePrefs, persistFilters],
  );

  const setFilterValues = useCallback(
    (action: React.SetStateAction<any>) => {
      setFilterValuesRaw((prev: any) => {
        const next = typeof action === "function" ? action(prev) : action;
        if (effectiveTableKey && hydratedFromTablePrefs.current && persistFilters) {
          updateTablePrefs({ inputFilters: next ?? {} }).catch(() => { });
        }
        return next;
      });
    },
    [effectiveTableKey, updateTablePrefs, persistFilters],
  );

  /**
   * When `groupBy.filterName` has a selected value, the grouped display collapses to a single
   * group labeled with that value instead of re-bucketing rows by their own field (see
   * `IGroupByConfig.filterName`). Filters unrelated to the grouping, or no groupBy at all,
   * leave this `null` and grouping (or plain rendering) behaves normally.
   */
  const groupByOverrideLabel = useMemo((): React.ReactNode | null => {
    if (!groupBy?.filterName) {
      return null;
    }
    const selected = filterValues?.[groupBy.filterName];
    const option = Array.isArray(selected) ? selected[0] : selected;
    const label = option?.name;
    return label != null && String(label).trim() !== "" ? String(label) : null;
  }, [groupBy, filterValues]);

  useEffect(() => {
    if (!pinnedInputFilters || Object.keys(pinnedInputFilters).length === 0) {
      return;
    }
    setFilterValues((prev: Record<string, unknown>) => {
      const next = { ...prev };
      let changed = false;
      for (const [key, value] of Object.entries(pinnedInputFilters)) {
        if (JSON.stringify(prev?.[key]) !== JSON.stringify(value)) {
          next[key] = value;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pinnedInputFilters, setFilterValues]);

  const setSorting = useCallback(
    (action: React.SetStateAction<ISorting>) => {
      setSortingRaw((prev) => {
        const next = typeof action === "function" ? action(prev) : action;
        if (effectiveTableKey && persistSorting && hydratedFromTablePrefs.current) {
          updateTablePrefs({ sorting: next }).catch(() => { });
        }
        return next;
      });
    },
    [effectiveTableKey, persistSorting, updateTablePrefs],
  );

  const setPagination = useCallback(
    (action: React.SetStateAction<IPagination2>) => {
      setPaginationRaw((prev) => {
        const next = typeof action === "function" ? action(prev) : action;
        if (
          effectiveTableKey &&
          hydratedFromTablePrefs.current &&
          persistPageSize
        ) {
          updateTablePrefs({ pageSize: next.pageSize }).catch(() => { });
        }
        return next;
      });
    },
    [effectiveTableKey, updateTablePrefs, persistPageSize],
  );

  const persistentFilterForLoader = useMemo(() => {
    if (!mergePanelFiltersIntoPersistentFilter) {
      return persistentFilter;
    }
    const base = asFilterRecordForPersistentMerge(persistentFilter);
    const vq = asFilterRecordForPersistentMerge(valueForQuery);
    const { quickFilter: _chip, ...rest } = vq;
    return { ...base, ...rest };
  }, [
    mergePanelFiltersIntoPersistentFilter,
    persistentFilter,
    valueForQuery,
  ]);

  const quickFilterDefs = useMemo(
    () => buildQuickFilterDefs(quickFilters, filtersData),
    [quickFilters, filtersData],
  );

  const { data: serverData, totalCount: serverTotalCount, quickFilterTotalCount } = useDataLoader<any>({
    endpoint,
    filters: valueForQuery,
    persistentFilter: persistentFilterForLoader,
    quickFilterTotalsPersistentFilter: persistentFilter,
    quickFilterDefs,
    pagination,
    sort: sorting,
    filterImmediateFlushKey,
    fetchQuickFilterTotals: quickFilters.length > 0,
    skipFetch: isClientDataMode,
  });

  const clientSearchTerm = String(filterValues?.inputSearch ?? "")
    .trim()
    .toLowerCase();

  const clientPreparedRows = useMemo(() => {
    if (!isClientDataMode) {
      return [];
    }
    const source = clientData ?? [];
    return formatData ? formatData(source) : source;
  }, [clientData, formatData, isClientDataMode]);

  const clientFilteredRows = useMemo(() => {
    if (!isClientDataMode) {
      return [];
    }
    if (!clientSearchTerm) {
      return clientPreparedRows;
    }
    return clientPreparedRows.filter((row) =>
      Object.values(row as Record<string, unknown>).some((value) =>
        String(value ?? "").toLowerCase().includes(clientSearchTerm),
      ),
    );
  }, [clientPreparedRows, clientSearchTerm, isClientDataMode]);

  const clientSortedRows = useMemo(() => {
    if (!isClientDataMode) {
      return [];
    }
    const field = sorting.field;
    if (!field) {
      return clientFilteredRows;
    }
    const column = effectiveColumnsDef.find((header) => header.id === field);
    if (column?.enableSorting === false) {
      return clientFilteredRows;
    }
    const direction = sorting.order === "asc" ? 1 : -1;
    return [...clientFilteredRows].sort((left, right) => {
      const result = compareClientRowValues(
        (left as Record<string, unknown>)[field],
        (right as Record<string, unknown>)[field],
      );
      return result * direction;
    });
  }, [clientFilteredRows, effectiveColumnsDef, isClientDataMode, sorting.field, sorting.order]);

  const clientTotalCount = clientSortedRows.length;

  const clientPagedRows = useMemo(() => {
    if (!isClientDataMode) {
      return [];
    }
    const start = pagination.pageIndex * pagination.pageSize;
    return clientSortedRows.slice(start, start + pagination.pageSize);
  }, [clientSortedRows, isClientDataMode, pagination.pageIndex, pagination.pageSize]);

  const data = isClientDataMode ? clientPagedRows : serverData;
  const totalCount = isClientDataMode ? clientTotalCount : serverTotalCount;

  useEffect(() => {
    onTotalCountChange?.(totalCount ?? 0);
  }, [totalCount, onTotalCountChange]);

  const resolvedBannerContent = useMemo(() => {
    if (!bannerContent) {
      return null;
    }
    if (typeof bannerContent === "function") {
      return bannerContent({
        quickFilterTotals: quickFilterTotalCount ?? {},
      });
    }
    return bannerContent;
  }, [bannerContent, quickFilterTotalCount]);

  const userInteractionCountRef = useRef(0);

  const buildQueryValue = useCallback(
    (
      fieldName: string,
      value: any,
      comparison: ComparisonType = "BY_ID",
      getPresetRange?: (optionId: string) => string
    ): void => {
      const cmp = comparison ?? "BY_ID";

      const clearFieldFromQuery = () => {
        setValueForQuery((prev: Record<string, QueryValue>) => {
          const next = { ...prev };
          delete next[fieldName];
          return next;
        });
      };

      if (
        COMPARISON_CLEAR_WHEN_SELECTION_EMPTY.has(cmp) &&
        Array.isArray(value) &&
        value.length === 0
      ) {
        clearFieldFromQuery();
        return;
      }

      if (cmp === "CONTAINS" && String(value ?? "").trim() === "") {
        clearFieldFromQuery();
        return;
      }

      if (
        cmp === "BETWEEN_TS_PRESET" &&
        (!Array.isArray(value) || value.length === 0)
      ) {
        clearFieldFromQuery();
        return;
      }

      const comparisonMap: Record<ComparisonType, () => QueryValue> = {
        CONTAINS: () => ({ value: String(value), comparisonType: "CONTAINS" }),
        BETWEEN: () => ({ value: String(value), comparisonType: "BETWEEN" }),
        BETWEEN_TS: () => ({
          value: convertDateRangeToEpoch(value),
          comparisonType: "BETWEEN_TS",
        }),
        BETWEEN_TS_PRESET: () => {
          const options = Array.isArray(value) ? value : [];
          const firstId = options[0]?.id ?? options[0]?.name;
          const range = firstId && getPresetRange ? getPresetRange(String(firstId)) : "";
          return { value: range, comparisonType: "BETWEEN_TS" };
        },
        BY_NAME: () => ({
          value: Array.isArray(value) ? value.map((v: { name: string }) => v.name) : [],
          comparisonType: "EQUAL",
        }),
        BY_NAME_CONTAINS: () => ({
          value: Array.isArray(value) ? value.map((v: { name: string }) => v.name) : [],
          comparisonType: "CONTAINS_ANY",
        }),
        ARRAY_SEARCH_ID: () => ({
          value: Array.isArray(value) ? value.map((v: { id: string }) => v.id) : [],
          comparisonType: "ARRAY_CONTAINS",
          arraySearchField: "id",
        }),
        ARRAY_CONTAINS: () => ({
          value: Array.isArray(value) ? value.map((v: { id: string }) => v.id) : [],
          comparisonType: "ARRAY_CONTAINS",
        }),
        BY_ID: () => ({
          value: Array.isArray(value) ? value.map((v: { id: string }) => v.id) : [],
        }),
        EQUAL: () => ({
          value: Array.isArray(value) ? value.map((v: { id: string }) => v.id) : [],
          comparisonType: "EQUAL",
        }),
        BOOLEAN: () => ({
          value: value.map((v: { name: string }) => v.name === "Yes"),
          comparisonType: "EQUAL",
        }),
        IN: () => ({
          value: Array.isArray(value) ? value.map((v: { id: string }) => v.id) : [],
          comparisonType: "IN",
        }),
        NOT_EQUAL_OR_NULL: () => ({
          value: Array.isArray(value) ? value.map((v: { id: string }) => v.id) : [],
          comparisonType: "NOT_EQUAL_OR_NULL",
        }),
        NOT_IN: () => ({
          value: Array.isArray(value) ? value.map((v: { id: string }) => v.id) : [],
          comparisonType: "NOT_IN",
        }),
        SPONSOR_NAME_TOKENS: () => ({
          value: Array.isArray(value) ? value.map((v: { id: string }) => String(v.id)) : [],
          comparisonType: "SPONSOR_NAME_TOKENS",
        }),
        IS_DEFINED: () => ({
          value: [],
          comparisonType: "IS_DEFINED",
        }),
      };

      if (cmp === "IS_DEFINED") {
        if (!Array.isArray(value) || value.length === 0) {
          clearFieldFromQuery();
          return;
        }
        setValueForQuery((prev: Record<string, QueryValue>) => ({
          ...prev,
          [fieldName]: comparisonMap.IS_DEFINED(),
        }));
        return;
      }

      const queryValue = comparisonMap[cmp]
        ? comparisonMap[cmp]()
        : comparisonMap.BY_ID();

      const built = queryValue.value;
      if (
        (Array.isArray(built) && built.length === 0) ||
        (typeof built === "string" && built.trim() === "")
      ) {
        clearFieldFromQuery();
        return;
      }

      setValueForQuery((prev: Record<string, QueryValue>) => ({
        ...prev,
        [fieldName]: queryValue,
      }));
    },
    [setValueForQuery]
  );

  const handleFilters = useCallback(
    (
      field: string,
      value: any,
      fieldName?: string,
      comparison?: string,
      getPresetRange?: (optionId: string) => string
    ) => {
      const _field = fieldName ?? field.toLocaleLowerCase();
      const currentQuickFilter = filterValues?.quickFilter;
      const coercedQuickFilter =
        _field === "year" && coerceQuickFilterOnYearChange
          ? coerceQuickFilterOnYearChange(
            value?.id ?? value ?? null,
            currentQuickFilter != null ? String(currentQuickFilter) : null,
          )
          : null;

      userInteractionCountRef.current += 1;
      if (coercedQuickFilter) {
        setFilterImmediateFlushKey((k) => k + 1);
        const previousChip = findQuickFilter(quickFilters, currentQuickFilter);
        const nextChip = findQuickFilter(quickFilters, coercedQuickFilter);
        if (nextChip) {
          setFilterValues((prev: any) => {
            const stripped = stripChipFromFilterValues(prev, previousChip);
            return applyChipToFilterValues(
              { ...stripped, [field]: value },
              nextChip,
            );
          });
          setValueForQuery((prev: Record<string, QueryValue>) => {
            const base = prev && typeof prev === "object" ? { ...prev } : {};
            const stripped = stripChipFromQuery(base, previousChip, filtersData);
            // Keep the year field the user just set (chip swap must not drop it).
            const withYear = { ...stripped } as Record<string, QueryValue>;
            return applyChipToQuery(
              withYear,
              nextChip,
              filtersData,
            ) as Record<string, QueryValue>;
          });
          buildQueryValue(
            _field,
            value,
            comparison as ComparisonType,
            getPresetRange,
          );
          return;
        }
      }
      setFilterValues((prev: any) => ({
        ...prev,
        [field]: value,
      }));
      buildQueryValue(_field, value, comparison as ComparisonType, getPresetRange);
    },
    [
      buildQueryValue,
      coerceQuickFilterOnYearChange,
      filterValues?.quickFilter,
      filtersData,
      quickFilters,
      setFilterValues,
      setValueForQuery,
    ]
  );

  const handleCustomFilterApply = useCallback(
    (
      panelName: string,
      apiFieldName: string | undefined,
      nextUiState: unknown,
      clause: CustomFilterApplyPayload,
      filterConfig?: IFilterData,
    ) => {
      const canUpdateQuery =
        clause == null ||
        !isSingleCustomFilterClause(clause) ||
        Boolean(apiFieldName) ||
        Boolean(filterConfig?.customQueryFieldNames?.length);

      userInteractionCountRef.current += 1;
      setFilterValues((prev: any) => ({ ...prev, [panelName]: nextUiState }));

      if (!canUpdateQuery) {
        return;
      }

      setValueForQuery((prev: Record<string, QueryValue>) => {
        const base = prev && typeof prev === "object" ? prev : {};
        const next = { ...base };
        applyCustomFilterClausesToQuery(
          next,
          apiFieldName,
          clause,
          filterConfig?.customQueryFieldNames,
        );
        return next;
      });
    },
    [setFilterValues, setValueForQuery],
  );

  const onSearchChange = useCallback((value: string) => {
    setFilterValues((prev: any) => ({ ...prev, inputSearch: value }));
    buildQueryValue('inputSearch', value, 'CONTAINS');
  }, [buildQueryValue, setFilterValues]);

  const selectedQuickFilter = filterValues?.quickFilter ?? null;

  const handleQuickFilterClick = useCallback(
    (flag: string) => {
      if (quickFilterRequireSelection && selectedQuickFilter === flag) {
        return;
      }
      const nextFlag = selectedQuickFilter === flag ? null : flag;
      userInteractionCountRef.current += 1;
      setFilterImmediateFlushKey((k) => k + 1);

      const previousChip = findQuickFilter(quickFilters, selectedQuickFilter);
      const nextChip = nextFlag
        ? findQuickFilter(quickFilters, nextFlag)
        : undefined;

      setFilterValues((prev: any) => {
        const stripped = stripChipFromFilterValues(prev, previousChip);
        if (!nextChip) {
          return { ...stripped, quickFilter: null };
        }
        return applyChipToFilterValues(stripped, nextChip);
      });
      setValueForQuery((prev: Record<string, QueryValue>) => {
        const base = prev && typeof prev === "object" ? { ...prev } : {};
        const stripped = stripChipFromQuery(base, previousChip, filtersData);
        if (!nextChip) {
          return stripped as Record<string, QueryValue>;
        }
        return applyChipToQuery(stripped, nextChip, filtersData) as Record<
          string,
          QueryValue
        >;
      });
    },
    [
      filtersData,
      quickFilterRequireSelection,
      quickFilters,
      selectedQuickFilter,
      setFilterValues,
      setValueForQuery,
    ]
  );

  const emptyFilterValuesRef = useRef(emptyFilterValues);
  emptyFilterValuesRef.current = emptyFilterValues;

  const legacyQuickFilterInQuery =
    valueForQuery &&
    typeof valueForQuery === "object" &&
    (valueForQuery as Record<string, unknown>).quickFilter != null &&
    String((valueForQuery as Record<string, unknown>).quickFilter) !== ""
      ? String((valueForQuery as Record<string, unknown>).quickFilter)
      : "";

  useLayoutEffect(() => {
    if (!quickFilterRequireSelection || quickFilters.length === 0) {
      return;
    }

    const validKeys = new Set(quickFilters.map((q) => q.key));
    const uiRaw = filterValues?.quickFilter;
    const uiStr =
      uiRaw != null && String(uiRaw) !== "" ? String(uiRaw) : null;

    const queryObj =
      valueForQuery && typeof valueForQuery === "object"
        ? (valueForQuery as Record<string, unknown>)
        : {};
    const qRaw = queryObj.quickFilter;
    const fromQuery =
      qRaw != null && String(qRaw) !== "" ? String(qRaw) : null;

    if (uiStr && validKeys.has(uiStr)) {
      const chip = findQuickFilter(quickFilters, uiStr);
      if (!chip) {
        return;
      }
      if (
        isQuickFilterSelectionResolved(
          chip,
          filterValues as Record<string, unknown>,
          queryObj,
          filtersData,
        )
      ) {
        return;
      }
      setValueForQuery((prev: Record<string, unknown>) => {
        const base =
          prev && typeof prev === "object" ? { ...prev } : {};
        const next = ensureChipHiddenQuery(base, chip);
        return next === base ? prev : next;
      });
      return;
    }

    if (fromQuery && validKeys.has(fromQuery)) {
      const chip = findQuickFilter(quickFilters, fromQuery);
      if (!chip) {
        return;
      }
      setFilterValues((prev: Record<string, unknown>) =>
        applyChipToFilterValues(prev, chip),
      );
      setValueForQuery((prev: Record<string, unknown>) => {
        const base =
          prev && typeof prev === "object" ? { ...prev } : {};
        const next = ensureChipHiddenQuery(base, chip);
        return next === base ? prev : next;
      });
      return;
    }

    const fallbackChip = quickFilters[0];
    if (
      isQuickFilterSelectionResolved(
        fallbackChip,
        filterValues as Record<string, unknown>,
        queryObj,
        filtersData,
      )
    ) {
      return;
    }

    const emptyDefaults = emptyFilterValuesRef.current;
    const seedInput = defaultQuickFilterSeed?.inputFilters ?? {};
    const seedQuery = defaultQuickFilterSeed?.query ?? {};
    userInteractionCountRef.current += 1;
    setFilterImmediateFlushKey((k) => k + 1);
    setFilterValues((prev: Record<string, unknown>) =>
      applyChipToFilterValues(
        { ...emptyDefaults, ...prev, ...seedInput },
        fallbackChip,
      ),
    );
    setValueForQuery((prev: Record<string, unknown>) =>
      applyChipToQuery(
        {
          ...(prev && typeof prev === "object" ? prev : {}),
          ...seedQuery,
        },
        fallbackChip,
        filtersData,
      ),
    );
  }, [
    quickFilterRequireSelection,
    quickFilters,
    filterValues?.quickFilter,
    legacyQuickFilterInQuery,
    setFilterValues,
    setValueForQuery,
    defaultQuickFilterSeed,
    filtersData,
  ]);

  useEffect(() => {
    if (!onQuickFilterChange) {
      return;
    }
    const raw = filterValues?.quickFilter;
    const key =
      raw != null && String(raw) !== ""
        ? String(raw)
        : quickFilters[0]?.key ?? "unmapped";
    onQuickFilterChange(key);
  }, [filterValues?.quickFilter, onQuickFilterChange, quickFilters]);

  useEffect(() => {
    if (!onQueryFilterChange) {
      return;
    }
    const raw =
      valueForQuery && typeof valueForQuery === "object"
        ? (valueForQuery as Record<string, unknown>)
        : {};
    const { quickFilter: _qf, ...serverFilter } = raw;
    onQueryFilterChange(serverFilter);
  }, [valueForQuery, onQueryFilterChange]);

  useEffect(() => {
    onSortingChange?.(sorting);
  }, [sorting, onSortingChange]);

  const prevQuickFilterForSortRef = useRef<string | null>(null);
  useEffect(() => {
    if (!quickFilterDefaultSorting || Object.keys(quickFilterDefaultSorting).length === 0) {
      return;
    }
    if (!hydratedFromTablePrefs.current) {
      return;
    }
    if (!applyQuickFilterSortOnChipChange && persistSorting && tablePrefs?.sorting) {
      return;
    }

    const raw = filterValues?.quickFilter;
    const key =
      raw != null && String(raw) !== ""
        ? String(raw)
        : quickFilters[0]?.key ?? null;
    if (!key) {
      return;
    }

    if (prevQuickFilterForSortRef.current === key) {
      return;
    }
    prevQuickFilterForSortRef.current = key;

    const def = quickFilterDefaultSorting[key];
    if (!def) {
      return;
    }

    if (applyQuickFilterSortOnChipChange) {
      setSorting((current) =>
        current.field === def.field && current.order === def.order ? current : def,
      );
      return;
    }

    setSortingRaw((current) =>
      current.field === def.field && current.order === def.order ? current : def,
    );
  }, [
    quickFilterDefaultSorting,
    applyQuickFilterSortOnChipChange,
    filterValues?.quickFilter,
    tablePrefs?.sorting,
    quickFilters,
    setSorting,
  ]);

  const selectedFilterGroups = useMemo<ISelectedFilterGroup[]>(() => {
    const fromMultiselect = filtersData
      .filter(
        (f) =>
          !isFilterHiddenFromUi(f) &&
          f.type !== "text" &&
          f.type !== "date" &&
          f.type !== "custom",
      )
      .map((f) => ({
        name: f.customTitle || f.name,
        key: f.name,
        values: Array.isArray(filterValues?.[f.name]) ? filterValues[f.name] : [],
      }))
      .filter((group) => group.values.length > 0);

    const fromCustom = filtersData
      .filter(
        (f) =>
          !isFilterHiddenFromUi(f) &&
          f.type === "custom" &&
          typeof f.getCustomSelectedChips === "function",
      )
      .map((f) => {
        const values = f.getCustomSelectedChips!(filterValues?.[f.name]) ?? [];
        if (values.length === 0) {
          return null;
        }
        return {
          name: f.customTitle || f.name,
          key: f.name,
          values,
        };
      })
      .filter(Boolean) as ISelectedFilterGroup[];

    return [...fromMultiselect, ...fromCustom];
  }, [filtersData, filterValues]);

  const hasPanelFilterSelections = selectedFilterGroups.length > 0;

  const handleRemoveSelectedValue = useCallback(
    (fieldName: string, valueToRemove: DropDownOptionBase) => {
      const filterConfig = filtersData.find((f) => f.name === fieldName);

      if (filterConfig?.type === "custom") {
        if (
          valueToRemove?.id != null &&
          String(valueToRemove.id) === CUSTOM_FILTER_CHIP_ID
        ) {
          const defaultValue =
            filterConfig.defaultCustomFilterValue !== undefined
              ? filterConfig.defaultCustomFilterValue
              : null;
          const defaultClauses =
            filterConfig.getDefaultQueryClauses?.() ??
            (filterConfig.getDefaultQueryClause && filterConfig.fieldName
              ? {
                [filterConfig.fieldName]:
                  filterConfig.getDefaultQueryClause() as CustomFilterQueryClause,
              }
              : null);

          if (
            filterConfig.fieldName ||
            filterConfig.customQueryFieldNames?.length ||
            filterConfig.getDefaultQueryClauses
          ) {
            handleCustomFilterApply(
              fieldName,
              filterConfig.fieldName,
              defaultValue,
              defaultClauses,
              filterConfig,
            );
          } else {
            userInteractionCountRef.current += 1;
            setFilterValues((prev: any) => ({ ...prev, [fieldName]: defaultValue }));
          }

          onPanelFilterReset?.([
            {
              name: fieldName,
              defaultValue,
              fieldName: filterConfig.fieldName,
            },
          ]);
        }
        return;
      }

      const currentValues = Array.isArray(filterValues?.[fieldName])
        ? filterValues[fieldName]
        : [];
      const nextValues = currentValues.filter((item: DropDownOptionBase) => {
        if (item.id != null && valueToRemove.id != null) {
          return String(item.id) !== String(valueToRemove.id);
        }
        return item.name !== valueToRemove.name;
      });

      const fc = filterConfig;
      handleFilters(
        fieldName,
        nextValues,
        fc?.fieldName,
        fc?.comparison,
        fc?.getPresetRange
      );
    },
    [
      filterValues,
      filtersData,
      handleFilters,
      handleCustomFilterApply,
      onPanelFilterReset,
      setFilterValues,
    ],
  );

  const handleClearAllSelectedFilters = useCallback(() => {
    userInteractionCountRef.current += 1;
    setFilterImmediateFlushKey((k) => k + 1);

    const activeChip = findQuickFilter(
      quickFilters,
      filterValues?.quickFilter,
    );

    const resets: PanelFilterResetInfo[] = [];
    filtersData.forEach((fc) => {
      if (isFilterHiddenFromUi(fc)) {
        return;
      }
      if (fc.type === "custom") {
        resets.push({
          name: fc.name,
          defaultValue:
            fc.defaultCustomFilterValue !== undefined
              ? fc.defaultCustomFilterValue
              : null,
          fieldName: fc.fieldName,
        });
      } else if (fc.type !== "text" && fc.type !== "date") {
        resets.push({ name: fc.name, defaultValue: [], fieldName: fc.fieldName });
      }
    });

    setFilterValues((prev: Record<string, any>) => {
      const next = { ...prev };
      filtersData.forEach((fc) => {
        if (isFilterHiddenFromUi(fc)) {
          return;
        }
        if (fc.type === "text") {
          next[fc.name] = "";
        }
        else if (fc.type === "date") {
          next[fc.name] = "";
        }
        else if (fc.type === "custom") {
          next[fc.name] =
            fc.defaultCustomFilterValue !== undefined
              ? fc.defaultCustomFilterValue
              : null;
        } else {
          next[fc.name] = [];
        }
      });
      if (activeChip) {
        return applyChipToFilterValues(next, activeChip);
      }
      next.quickFilter = prev.quickFilter ?? null;
      return next;
    });

    setValueForQuery((prev: Record<string, QueryValue>) => {
      const next = { ...prev };
      filtersData.forEach((fc) => {
        if (isFilterHiddenFromUi(fc)) {
          return;
        }
        if (fc.type === "custom") {
          const defaultClauses =
            fc.getDefaultQueryClauses?.() ??
            (fc.getDefaultQueryClause && fc.fieldName
              ? { [fc.fieldName]: fc.getDefaultQueryClause() as CustomFilterQueryClause }
              : null);
          applyCustomFilterClausesToQuery(
            next,
            fc.fieldName,
            defaultClauses,
            fc.customQueryFieldNames,
          );
        } else {
          const apiField = fc.fieldName ?? fc.name.toLocaleLowerCase();
          delete next[apiField];
        }
      });
      delete (next as Record<string, unknown>).quickFilter;
      if (activeChip) {
        return applyChipToQuery(next, activeChip, filtersData) as Record<
          string,
          QueryValue
        >;
      }
      return next;
    });

    onPanelFilterReset?.(resets);
  }, [
    filtersData,
    filterValues?.quickFilter,
    onPanelFilterReset,
    quickFilters,
    setFilterValues,
    setValueForQuery,
  ]);

  useEffect(() => {
    setColumnOrder((prev) => {
      const defaultSet = new Set(defaultOrder);
      const prevSet = new Set(prev);
      const columnsChanged =
        defaultSet.size !== prevSet.size || defaultOrder.some((id) => !prevSet.has(id));
      return normalizeColumnOrder(
        coalesceGroupedColumnOrder(
          columnsChanged ? defaultOrder : prev,
          effectiveColumnsDef,
        ),
        effectiveColumnsDef,
      );
    });
    setColumnVisibility((prev) => {
      const next: Record<string, boolean> = { ...prev };
      effectiveColumnsDef.forEach((h) => {
        if (next[h.id] === undefined) {
          next[h.id] =
            defaultColumnVisibility?.[h.id] ?? h.defaultVisibility ?? true;
        }
      });
      Object.keys(next).forEach((key) => {
        if (!effectiveColumnsDef.some((h) => h.id === key)) {
          delete next[key];
        }
      });
      return next;
    });
  }, [effectiveColumnsDef, defaultOrder, defaultColumnVisibility]);

  useEffect(() => {
    if (!effectiveTableKey || !tablePrefsReady) {
      return;
    }
    if (hydratedColumnPrefs.current) {
      return;
    }

    const tp = tablePrefs;
    const order = tp?.columnOrder?.filter((id) =>
      effectiveColumnsDef.some((h) => h.id === id),
    );
    const vis = tp?.columnVisibility;
    if (order?.length) {
      const rest = defaultOrder.filter((id) => !order.includes(id));
      setColumnOrder(
        normalizeColumnOrder(
          coalesceGroupedColumnOrder([...order, ...rest], effectiveColumnsDef),
          effectiveColumnsDef,
        ),
      );
    }
    if (vis && Object.keys(vis).length) {
      setColumnVisibility((prev) => {
        const next = { ...prev };
        effectiveColumnsDef.forEach((h) => {
          if (vis[h.id] !== undefined) {
            next[h.id] = vis[h.id];
          }
        });
        return next;
      });
    }
    hydratedColumnPrefs.current = true;
  }, [effectiveTableKey, tablePrefsReady, effectiveColumnsDef, defaultOrder]);

  useEffect(() => {
    if (!effectiveTableKey || !tablePrefsReady) {
      return;
    }

    const widths = tablePrefs?.columnWidths;
    if (!widths || Object.keys(widths).length === 0) {
      return;
    }

    const prefsKey = effectiveColumnsDef
      .map((h) => {
        const w = widths[h.id];
        return w != null && w > 0 ? `${h.id}:${w}` : "";
      })
      .filter(Boolean)
      .join("|");

    if (!prefsKey || hydratedColumnWidthsKeyRef.current === prefsKey) {
      return;
    }
    hydratedColumnWidthsKeyRef.current = prefsKey;

    const filteredWidths: Record<string, number> = {};
    effectiveColumnsDef.forEach((h) => {
      const w = widths[h.id];
      if (w != null && w > 0) {
        filteredWidths[h.id] = w;
      }
    });
    setResizedWidths(filteredWidths);
  }, [effectiveTableKey, tablePrefsReady, tablePrefs?.columnWidths, effectiveColumnsDef]);

  const formattedTableData = useMemo(
    () => (isClientDataMode ? data : formatData ? formatData(data) : data),
    [data, formatData, isClientDataMode],
  );

  const selectionContextValue =
    useMemo<FilteredTableSelectionContextValue | null>(() => {
      if (!rowSelection) {
        return null;
      }
      const getRowId =
        rowSelection.getRowId ?? ((row: any) => String(row?.id ?? ""));
      const pageRowIds = (formattedTableData ?? [])
        .map((row: any) => getRowId(row))
        .filter((id: string) => id !== "");
      const isRowSelectable = rowSelection.isRowSelectable;
      const selectablePageRowIds = isRowSelectable
        ? (formattedTableData ?? [])
          .filter((row: any) => isRowSelectable(row))
          .map((row: any) => getRowId(row))
          .filter((id: string) => id !== "")
        : pageRowIds;
      return {
        selectedIds: rowSelection.selectedIds,
        onToggleRow: rowSelection.onToggleRow,
        onTogglePage: rowSelection.onTogglePage,
        getRowId,
        isRowSelectable,
        pageRowIds,
        selectablePageRowIds,
      };
    }, [rowSelection, formattedTableData]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!active?.id || !over?.id || active.id === over.id) {
        return;
      }

      const fixedPrefix = getFixedColumnPrefixIds(effectiveColumnsDef);
      const fixedSet = new Set(fixedPrefix);
      const activeId = active.id as string;
      if (fixedSet.has(activeId)) {
        return;
      }

      setColumnOrder((prevOrder) => {
        const overId = over.id as string;
        const activeBlockStart = prevOrder.indexOf(activeId);
        const overBlockStart = prevOrder.indexOf(overId);
        if (activeBlockStart === -1 || overBlockStart === -1) {
          return prevOrder;
        }
        if (activeBlockStart < fixedPrefix.length || overBlockStart < fixedPrefix.length) {
          return prevOrder;
        }

        const next = moveGroupedColumnOrder(
          prevOrder,
          activeId,
          overId,
          effectiveColumnsDef,
        );
        if (effectiveTableKey) {
          updateTablePrefs({ columnOrder: next }).catch(() => { });
        }
        return next;
      });
    },
    [effectiveTableKey, updateTablePrefs, effectiveColumnsDef]
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const orderedHeaders = useMemo(() => {
    const byId = new Map(effectiveColumnsDef.map((h) => [h.id, h] as const));
    return columnOrder.map((id) => byId.get(id)).filter(Boolean) as IHeader[];
  }, [effectiveColumnsDef, columnOrder]);

  const visibleHeaders = useMemo(() => {
    return orderedHeaders.filter((h) => columnVisibility[h.id] !== false);
  }, [orderedHeaders, columnVisibility]);

  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isSyncingHorizontalScrollRef = useRef(false);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [horizontalContentWidth, setHorizontalContentWidth] = useState(0);
  const [horizontalViewportWidth, setHorizontalViewportWidth] = useState(0);
  const [horizontalScrollLeft, setHorizontalScrollLeft] = useState(0);
  const quickFilterBarMeasureRef = useRef<HTMLDivElement>(null);
  const [quickFilterBarHeightPx, setQuickFilterBarHeightPx] = useState(0);

  useLayoutEffect(() => {
    const stickyTopBar =
      stickyQuickFilterBar &&
      (quickFilterBarPosition === "top" || quickFilterBarPosition === "both");
    if (!stickyTopBar) {
      setQuickFilterBarHeightPx(0);
      return;
    }
    const el = quickFilterBarMeasureRef.current;
    if (!el) {
      return;
    }
    const measure = () =>
      setQuickFilterBarHeightPx(Math.round(el.getBoundingClientRect().height));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stickyQuickFilterBar, quickFilterBarPosition]);
  const scrollAnimationRef = useRef<number | null>(null);
  const targetScrollRef = useRef<number | null>(null);

  const getColumnWidth = useCallback(
    (header: IHeader): number | undefined => {
      if (resizedWidths[header.id]) {
        return resizedWidths[header.id];
      }
      if (header.width && header.width > 0) {
        return header.width;
      }
      return measuredWidths[header.id];
    },
    [resizedWidths, measuredWidths]
  );

  const hasAllWidths = useMemo(
    () => visibleHeaders.every((h) => getColumnWidth(h) !== undefined),
    [visibleHeaders, getColumnWidth]
  );

  const computedTableWidth = useMemo(() => {
    if (!hasAllWidths) {
      return 0;
    }
    return visibleHeaders.reduce(
      (sum, header) => sum + (getColumnWidth(header) ?? 0),
      0,
    );
  }, [hasAllWidths, visibleHeaders, getColumnWidth]);

  const horizontalScrollThumb = useMemo(() => {
    if (!hasHorizontalScroll || horizontalContentWidth <= 0 || horizontalViewportWidth <= 0) {
      return { thumbWidth: 0, thumbLeft: 0 };
    }
    const thumbWidth = Math.max(
      32,
      (horizontalViewportWidth / horizontalContentWidth) * horizontalViewportWidth,
    );
    const maxScroll = horizontalContentWidth - horizontalViewportWidth;
    const maxThumbLeft = Math.max(horizontalViewportWidth - thumbWidth, 0);
    const thumbLeft =
      maxScroll > 0 ? (horizontalScrollLeft / maxScroll) * maxThumbLeft : 0;
    return { thumbWidth, thumbLeft };
  }, [
    hasHorizontalScroll,
    horizontalContentWidth,
    horizontalViewportWidth,
    horizontalScrollLeft,
  ]);

  const colgroup = useMemo(
    () => (
      <colgroup>
        {visibleHeaders.map((h) => {
          const w = getColumnWidth(h);
          return <col key={h.id} style={w ? { width: `${w}px` } : undefined} />;
        })}
      </colgroup>
    ),
    [visibleHeaders, getColumnWidth]
  );

  const handleColumnResize = useCallback(
    (columnId: string, newWidth: number) => {
      setResizedWidths((prev) => {
        const next = { ...prev, [columnId]: newWidth };
        if (effectiveTableKey) {
          updateTablePrefs({ columnWidths: next }).catch(() => { });
        }
        return next;
      });
    },
    [effectiveTableKey, updateTablePrefs],
  );

  const visibleHeaderIdsKey = useMemo(() => visibleHeaders.map((h) => h.id).join("|"), [visibleHeaders]);

  useLayoutEffect(() => {
    const headerScroll = headerScrollRef.current;
    if (!headerScroll) {
      return;
    }

    const measure = () => {
      const next: Record<string, number> = {};
      const ths = headerScroll.querySelectorAll<HTMLTableCellElement>("th[data-col-id]");
      ths.forEach((th) => {
        const id = th.dataset.colId;
        if (!id) {
          return;
        }
        next[id] = Math.ceil(th.getBoundingClientRect().width);
      });
      setMeasuredWidths((prev) => ({ ...prev, ...next }));
    };

    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) {
      ro.observe(headerScroll);
    }
    window.addEventListener("resize", measure);

    return () => {
      if (ro) {
        ro.disconnect();
      }
      window.removeEventListener("resize", measure);
    };
  }, [visibleHeaderIdsKey, visibleHeaders.length]);

  const handleSortingClick = useCallback(
    (headerId: string) => {
      const header = effectiveColumnsDef.find((h) => h.id === headerId);
      if (!header || header.enableSorting === false) {
        return;
      }
      userHasChangedSortingRef.current = true;
      setSorting(nextSort(sorting, headerId));
    },
    [effectiveColumnsDef, setSorting, sorting]
  );

  const handlePageChange = useCallback((pageNumber: number, pageSize: number) => {
    setPagination({
      pageIndex: Math.max(0, pageNumber - 1),
      pageSize: normalizeTablePageSize(pageSize),
    });
  }, []);

  const toggleColumnVisibility = useCallback(
    (columnId: string) => {
      setColumnVisibility((prev) => {
        const next = { ...prev, [columnId]: !(prev[columnId] ?? true) };
        if (effectiveTableKey) {
          updateTablePrefs({ columnVisibility: next }).catch(() => { });
        }
        return next;
      });
    },
    [effectiveTableKey, updateTablePrefs]
  );

  const clearAllColumnVisibility = useCallback(() => {
    setColumnVisibility((prev) => {
      const next = { ...prev };
      effectiveColumnsDef.forEach((h) => {
        if (!COLUMN_VISIBILITY_PANEL_IDS.has(h.id)) {
          next[h.id] = false;
        }
      });
      if (effectiveTableKey) {
        updateTablePrefs({ columnVisibility: next }).catch(() => { });
      }
      return next;
    });
  }, [effectiveColumnsDef, effectiveTableKey, updateTablePrefs]);

  const selectAllColumnVisibility = useCallback(() => {
    setColumnVisibility((prev) => {
      const next = { ...prev };
      effectiveColumnsDef.forEach((h) => {
        if (!COLUMN_VISIBILITY_PANEL_IDS.has(h.id)) {
          next[h.id] = true;
        }
      });
      if (effectiveTableKey) {
        updateTablePrefs({ columnVisibility: next }).catch(() => { });
      }
      return next;
    });
  }, [effectiveColumnsDef, effectiveTableKey, updateTablePrefs]);

  const syncHorizontalScrollLeft = useCallback((scrollLeft: number) => {
    if (isSyncingHorizontalScrollRef.current) {
      return;
    }
    isSyncingHorizontalScrollRef.current = true;
    const body = bodyScrollRef.current;
    const header = headerScrollRef.current;
    if (body) {
      body.scrollLeft = scrollLeft;
    }
    if (header) {
      header.scrollLeft = scrollLeft;
    }
    setHorizontalScrollLeft(scrollLeft);
    isSyncingHorizontalScrollRef.current = false;
  }, []);

  const horizontalMaxScrollLeft = useMemo(
    () => Math.max(horizontalContentWidth - horizontalViewportWidth, 0),
    [horizontalContentWidth, horizontalViewportWidth],
  );

  const handleHorizontalScrollLeftChange = useCallback(
    (scrollLeft: number) => {
      const body = bodyScrollRef.current;
      const domMaxScroll = body
        ? Math.max(body.scrollWidth - body.clientWidth, 0)
        : 0;
      const maxScroll = Math.max(horizontalMaxScrollLeft, domMaxScroll);
      syncHorizontalScrollLeft(Math.max(0, Math.min(maxScroll, scrollLeft)));
    },
    [syncHorizontalScrollLeft, horizontalMaxScrollLeft],
  );

  const handleBodyHorizontalScroll = useCallback(() => {
    const body = bodyScrollRef.current;
    if (!body) {
      return;
    }
    syncHorizontalScrollLeft(body.scrollLeft);
  }, [syncHorizontalScrollLeft]);

  useLayoutEffect(() => {
    if (!showTopHorizontalScrollBar) {
      setHasHorizontalScroll(false);
      setHorizontalContentWidth(0);
      setHorizontalViewportWidth(0);
      setHorizontalScrollLeft(0);
      return;
    }

    const body = bodyScrollRef.current;
    if (!body) {
      return;
    }

    const measureHorizontalScroll = () => {
      const bodyTable = body.querySelector("table");
      const headerTable = headerScrollRef.current?.querySelector("table");
      const domContentWidth = Math.max(
        bodyTable?.scrollWidth ?? 0,
        headerTable?.scrollWidth ?? 0,
        body.scrollWidth,
      );
      const contentWidth = Math.max(computedTableWidth, domContentWidth);
      const viewportWidth = body.clientWidth;
      const canScroll = contentWidth - viewportWidth > 1;

      setHasHorizontalScroll(canScroll);
      setHorizontalContentWidth(contentWidth);
      setHorizontalViewportWidth(viewportWidth);
      setHorizontalScrollLeft(body.scrollLeft);
    };

    const scheduleMeasure = () => {
      window.requestAnimationFrame(measureHorizontalScroll);
    };

    scheduleMeasure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleMeasure) : null;
    ro?.observe(body);
    const table = body.querySelector("table");
    if (table && ro) {
      ro.observe(table);
    }
    if (headerScrollRef.current && ro) {
      ro.observe(headerScrollRef.current);
    }
    if (tableContainerRef.current && ro) {
      ro.observe(tableContainerRef.current);
    }
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [
    showTopHorizontalScrollBar,
    visibleHeaderIdsKey,
    formattedTableData.length,
    hasAllWidths,
    computedTableWidth,
    measuredWidths,
    resizedWidths,
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") {
        return;
      }

      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        target.closest("input") ||
        target.closest("textarea")
      ) {
        return;
      }

      const body = bodyScrollRef.current;
      const header = headerScrollRef.current;
      const container = tableContainerRef.current;
      if (!body || !header || !container) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      if (containerRect.width === 0 || containerRect.height === 0) {
        return;
      }

      const findScrollContainer = (): HTMLElement | null => {
        if (body.scrollWidth > body.clientWidth) {
          return body;
        }

        let parent: HTMLElement | null = container.parentElement;
        while (parent) {
          const style = window.getComputedStyle(parent);
          const hasOverflowX =
            style.overflowX === "auto" ||
            style.overflowX === "scroll" ||
            style.overflow === "auto" ||
            style.overflow === "scroll";

          if (hasOverflowX && parent.scrollWidth > parent.clientWidth) {
            return parent;
          }

          if (parent.scrollWidth > parent.clientWidth) {
            const currentScroll = parent.scrollLeft;
            parent.scrollLeft = currentScroll + 1;
            const canScroll = parent.scrollLeft !== currentScroll;
            parent.scrollLeft = currentScroll;
            if (canScroll) {
              return parent;
            }
          }

          parent = parent.parentElement;
        }

        return null;
      };

      const scrollContainer = findScrollContainer();
      if (!scrollContainer) {
        return;
      }

      const scrollAmount = 50;
      const scrollDelta = e.key === "ArrowLeft" ? -scrollAmount : scrollAmount;
      e.preventDefault();

      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const currentScroll = scrollContainer.scrollLeft;
      const newTargetScroll =
        targetScrollRef.current !== null
          ? Math.max(0, Math.min(maxScroll, targetScrollRef.current + scrollDelta))
          : Math.max(0, Math.min(maxScroll, currentScroll + scrollDelta));

      targetScrollRef.current = newTargetScroll;
      if (scrollAnimationRef.current !== null) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }

      const animateScroll = () => {
        const current = scrollContainer.scrollLeft;
        const targetValue = targetScrollRef.current ?? current;
        const distance = targetValue - current;

        if (Math.abs(distance) < 1) {
          syncHorizontalScrollLeft(targetValue);
          scrollAnimationRef.current = null;
          targetScrollRef.current = null;
          return;
        }

        const step = distance * 0.15;
        const newScroll = current + step;
        scrollContainer.scrollLeft = newScroll;
        syncHorizontalScrollLeft(newScroll);
        scrollAnimationRef.current = requestAnimationFrame(animateScroll);
      };

      scrollAnimationRef.current = requestAnimationFrame(animateScroll);
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      if (scrollAnimationRef.current !== null) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
      targetScrollRef.current = null;
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [syncHorizontalScrollLeft]);

  const exportToExcel = async (currentData: boolean) => {
    try {
      let sourceData: any[];
      if (isClientDataMode) {
        sourceData = currentData ? clientPagedRows : clientSortedRows;
      } else if (currentData) {
        sourceData = data;
      } else {
        const params = buildQueryParamsPersistentFilterOnly(persistentFilter);
        const response = await axios.Get(`${endpoint}?${params.toString()}`);
        sourceData = response.data;
      }
      if (!sourceData?.length) {
        return;
      }

      let dataForExcel: any[];
      if (isClientDataMode) {
        dataForExcel = sourceData;
      } else if (formatExcelData) {
        const result = formatExcelData(sourceData, currentData);
        dataForExcel = result instanceof Promise ? await result : result;
      } else if (formatData) {
        dataForExcel = formatData(sourceData);
      } else {
        dataForExcel = sourceData;
      }

      const exportColumns = getVisibleExcelColumns(
        effectiveColumnsDef,
        excelHeaders,
        columnVisibility,
        currentData
      );

      const rows = dataForExcel.map((row: any) => {
        const out: Record<string, any> = {};
        exportColumns.forEach((col) => {
          const resolved = row?.[col.id];
          const colTitle = getExcelColumnTitle(col);
          out[colTitle] = safeExcelValue(resolved);
        });
        return out;
      });

      const outputFilename = excelFilenameProp ?? buildExcelFilename(endpoint);
      await exportStyledExcel({
        filename: outputFilename,
        title: getExcelTitleFromFilename(outputFilename),
        rows,
        columns: exportColumns.map((col) => ({
          key: getExcelColumnTitle(col),
          header: getExcelColumnTitle(col),
        })),
      });
    } catch (e) {
      console.error("Error al exportar a Excel:", e);
    } finally {
      setOpenExcelModal(false);
    }
  };

  const showTopQuickFilterBar =
    (quickFilters.length > 0 || !!quickFilterBarAccessory) &&
    (quickFilterBarPosition === "top" || quickFilterBarPosition === "both");
  const showBelowSearchQuickFilterBar =
    (quickFilters.length > 0 || !!quickFilterBarAccessory) &&
    (quickFilterBarPosition === "belowSearch" ||
      quickFilterBarPosition === "both");
  const stickyTopQuickFilterBar =
    stickyQuickFilterBar &&
    (quickFilterBarPosition === "top" || quickFilterBarPosition === "both");
  const resolvedBelowSearchContent =
    showBelowSearchQuickFilterBar ? (
      <>
        <QuickFiltersBar
          quickFilters={quickFilters}
          selectedQuickFilter={selectedQuickFilter}
          quickFilterTotalCount={quickFilterTotalCount ?? {}}
          onQuickFilterClick={handleQuickFilterClick}
          quickFilterBarAccessory={quickFilterBarAccessory}
          lockActiveQuickFilter={quickFilterRequireSelection}
          variant="panel"
          roundedTop={false}
          sticky={false}
        />
        {belowSearchContent}
      </>
    ) : (
      belowSearchContent
    );

  return (
    <div
      className={`flex w-full min-w-0 flex-col self-stretch${embeddedInScrollContainer ? " min-h-0 flex-1" : ""
        }`}
    >
      {showExcelExport && (
        <ExcelModal
          open={openExcelModal}
          setOpen={setOpenExcelModal}
          dataExcelExport={exportToExcel}
        />
      )}

      <div
        className={
          stickyTopQuickFilterBar
            ? "relative"
            : "contents"
        }
      >
        {showTopQuickFilterBar && (
          <QuickFiltersBar
            ref={quickFilterBarMeasureRef}
            quickFilters={quickFilters}
            selectedQuickFilter={selectedQuickFilter}
            quickFilterTotalCount={quickFilterTotalCount ?? {}}
            onQuickFilterClick={handleQuickFilterClick}
            quickFilterBarAccessory={quickFilterBarAccessory}
            lockActiveQuickFilter={quickFilterRequireSelection}
            sticky={stickyTopQuickFilterBar}
          />
        )}

        <FiltersPanel
          containerclassName={containerclassName}
          layout={filtersPanelLayout}
          bannerContent={resolvedBannerContent}
          showSelectedFilters={showSelectedFilters}
          inlineToolbar={inlineFilterToolbar}
          filtersData={filtersData}
          filterValues={filterValues}
          onHandleFilters={handleFilters}
          hasSelectedFilters={hasPanelFilterSelections}
          selectedFilterGroups={selectedFilterGroups}
          onClearAllSelectedFilters={handleClearAllSelectedFilters}
          onRemoveSelectedValue={handleRemoveSelectedValue}
          showSearchInput={showSearchInput}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          searchLeading={searchLeading}
          searchToolbarAccessory={searchToolbarAccessory}
          belowSearchContent={resolvedBelowSearchContent}
          showExcelExport={showExcelExport}
          onOpenExcelModal={() => setOpenExcelModal(true)}
          showColumnVisibility={showColumnVisibility}
          columnsDef={effectiveColumnsDef}
          columnVisibility={columnVisibility}
          onToggleColumnVisibility={toggleColumnVisibility}
          onClearAllColumnVisibility={clearAllColumnVisibility}
          onSelectAllColumnVisibility={selectAllColumnVisibility}
          onCustomFilterApply={(panelName, apiFieldName, nextUiState, clause) => {
            const filterConfig = filtersData.find((f) => f.name === panelName);
            handleCustomFilterApply(
              panelName,
              apiFieldName,
              nextUiState,
              clause,
              filterConfig,
            );
          }}
        />

        <FilteredTableSelectionProvider value={selectionContextValue}>
          <TableSection
            containerclassName={containerclassName}
            embeddedInScrollContainer={embeddedInScrollContainer}
            topPagination={topPagination}
            bottomPagination={bottomPagination}
            totalCount={totalCount}
            pagination={pagination}
            onPageChange={handlePageChange}
            sensors={sensors}
            onDragEnd={handleDragEnd}
            tableContainerRef={tableContainerRef}
            topContent={topContent}
            showTopHorizontalScrollBar={showTopHorizontalScrollBar}
            hasHorizontalScroll={hasHorizontalScroll}
            horizontalViewportWidth={horizontalViewportWidth}
            horizontalScrollThumbWidth={horizontalScrollThumb.thumbWidth}
            horizontalScrollThumbLeft={horizontalScrollThumb.thumbLeft}
            horizontalMaxScrollLeft={horizontalMaxScrollLeft}
            onHorizontalScrollLeftChange={handleHorizontalScrollLeftChange}
            headerScrollRef={headerScrollRef}
            bodyScrollRef={bodyScrollRef}
            onBodyHorizontalScroll={handleBodyHorizontalScroll}
            hasAllWidths={hasAllWidths}
            colgroup={colgroup}
            visibleHeaders={visibleHeaders}
            sorting={sorting}
            onSortingClick={handleSortingClick}
            getColumnWidth={getColumnWidth}
            onColumnResize={handleColumnResize}
            formattedTableData={formattedTableData}
            emptyStateMessage={emptyStateMessage}
            stickyHeaderOffsetPx={
              stickyTopQuickFilterBar ? quickFilterBarHeightPx : undefined
            }
            groupBy={groupBy}
            groupByOverrideLabel={groupByOverrideLabel}
          />
        </FilteredTableSelectionProvider>
      </div>
    </div>
  );
}
